/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useEffect, useMemo } from "react";
import { useDirectOrder } from "./useDirectOrder";
import { useAuth } from "./useAuth";
import usePluginUtils from "./usePluginUtils";
import { useGetPurchasedMemberships } from "../lib/react-query/useGetPurchasedMemberships";
import { CART_LINE_TYPES } from "../constants/constants";

export const useSyncMembershipPrices = () => {
  const { line, repriceDirectOrder } = useDirectOrder();
  const { isAuthenticated } = useAuth();
  const { isPluginEnabled } = usePluginUtils();
  const membershipPluginEnabled = isPluginEnabled("membership");

  const membershipLine =
    membershipPluginEnabled && line?.type === CART_LINE_TYPES.MEMBERSHIP ? line : null;
  const hasMembershipLine = !!membershipLine;

  const { data: purchasedData, isSuccess } = useGetPurchasedMemberships(
    { "filter[active_only]": 0, on_page: 100 },
    { enabled: isAuthenticated && hasMembershipLine }
  );

  const purchasedMembershipIds = useMemo(() => {
    const ids = new Set();
    (purchasedData?.data || []).forEach((purchase) => {
      ids.add(+purchase.membership_id);
    });
    return ids;
  }, [purchasedData]);

  useEffect(() => {
    if (!hasMembershipLine) return;
    if (isAuthenticated && !isSuccess) return;

    const snap = membershipLine.snapshot;
    if (!snap || snap.first_price == null) return;

    const ownsRecurring =
      isAuthenticated && snap.is_recurring && purchasedMembershipIds.has(+snap.id);
    const desired = ownsRecurring ? snap.recurring_price : snap.first_price;

    if (desired != null && desired !== snap.price) {
      repriceDirectOrder({ price: desired });
    }
  }, [
    hasMembershipLine,
    membershipLine,
    isAuthenticated,
    isSuccess,
    purchasedMembershipIds,
    repriceDirectOrder,
  ]);
};
